import * as THREE from 'three';
import { Measure } from './Measure.js';
import { Utils } from '../utils.js';
import { CameraMode } from '../defines.js';
import { EventDispatcher } from '../EventDispatcher.js';

function updateAzimuth(viewer, measure) {
    const azimuth = measure.azimuth;

    const isOkay = measure.points.length === 2;

    azimuth.node.visible = isOkay && measure.showAzimuth;

    if (!azimuth.node.visible) {
        return;
    }

    const camera = viewer.scene.getActiveCamera();
    const renderAreaSize = viewer.renderer.getSize(new THREE.Vector2());
    const width = renderAreaSize.width;
    const height = renderAreaSize.height;

    const [p0, p1] = measure.points;
    const r = p0.position.distanceTo(p1.position);
    const northVec = Utils.getNorthVec(p0.position, r, viewer.getProjection());
    const northPos = p0.position.clone().add(northVec);

    azimuth.center.position.copy(p0.position);
    azimuth.center.scale.set(2, 2, 2);

    azimuth.center.visible = false;
    // azimuth.target.visible = false;

    {
        // north
        azimuth.north.position.copy(northPos);
        azimuth.north.scale.set(2, 2, 2);

        let distance = azimuth.north.position.distanceTo(camera.position);
        let pr = Utils.projectedRadius(1, camera, distance, width, height);

        let scale = 5 / pr;
        azimuth.north.scale.set(scale, scale, scale);
    }

    {
        // target
        azimuth.target.position.copy(p1.position);
        azimuth.target.position.z = azimuth.north.position.z;

        let distance = azimuth.target.position.distanceTo(camera.position);
        let pr = Utils.projectedRadius(1, camera, distance, width, height);

        let scale = 5 / pr;
        azimuth.target.scale.set(scale, scale, scale);
    }

    azimuth.circle.position.copy(p0.position);
    azimuth.circle.scale.set(r, r, r);
    azimuth.circle.material.resolution.set(width, height);

    // to target
    azimuth.centerToTarget.geometry.setPositions([0, 0, 0, ...p1.position.clone().sub(p0.position).toArray()]);
    azimuth.centerToTarget.position.copy(p0.position);
    azimuth.centerToTarget.geometry.computeBoundingSphere();
    azimuth.centerToTarget.computeLineDistances();
    azimuth.centerToTarget.material.resolution.set(width, height);

    // to target ground
    azimuth.centerToTargetground.geometry.setPositions([0, 0, 0, p1.position.x - p0.position.x, p1.position.y - p0.position.y, 0]);
    azimuth.centerToTargetground.position.copy(p0.position);
    azimuth.centerToTargetground.geometry.computeBoundingSphere();
    azimuth.centerToTargetground.computeLineDistances();
    azimuth.centerToTargetground.material.resolution.set(width, height);

    // to north
    azimuth.centerToNorth.geometry.setPositions([0, 0, 0, northPos.x - p0.position.x, northPos.y - p0.position.y, 0]);
    azimuth.centerToNorth.position.copy(p0.position);
    azimuth.centerToNorth.geometry.computeBoundingSphere();
    azimuth.centerToNorth.computeLineDistances();
    azimuth.centerToNorth.material.resolution.set(width, height);

    // label
    const radians = Utils.computeAzimuth(p0.position, p1.position, viewer.getProjection());
    let degrees = THREE.MathUtils.radToDeg(radians);
    if (degrees < 0) {
        degrees = 360 + degrees;
    }
    const txtDegrees = `${degrees.toFixed(2)}°`;
    const labelDir = northPos.clone().add(p1.position).multiplyScalar(0.5).sub(p0.position);
    if (labelDir.length() > 0) {
        labelDir.z = 0;
        labelDir.normalize();
        const labelVec = labelDir.clone().multiplyScalar(r);
        const labelPos = p0.position.clone().add(labelVec);
        azimuth.label.position.copy(labelPos);
    }
    azimuth.label.setText(txtDegrees);
    let distance = azimuth.label.position.distanceTo(camera.position);
    let pr = Utils.projectedRadius(1, camera, distance, width, height);
    let scale = 70 / pr;
    azimuth.label.scale.set(scale, scale, scale);
}

export class MeasuringTool extends EventDispatcher {
    constructor(viewer) {
        super();

        this.viewer = viewer;
        this.renderer = viewer.renderer;

        this.addEventListener('start_inserting_measurement', (e) => {
            this.viewer.dispatchEvent({
                type: 'cancel_insertions',
            });
        });

        this.showLabels = true;
        this.scene = new THREE.Scene();
        this.scene.name = 'scene_measurement';
        this.light = new THREE.PointLight(0xffffff, 2.0);
        this.light.decay = 0;
        this.scene.add(this.light);
        this.needCalibration = true;

        this.viewer.inputHandler.registerInteractiveScene(this.scene);

        this.onRemove = (e) => {
            this.scene.remove(e.measurement);
        };
        this.onAdd = (e) => {
            this.scene.add(e.measurement);
        };

        for (let measurement of viewer.scene.measurements) {
            this.onAdd({ measurement: measurement });
        }

        viewer.addEventListener('update', this.update.bind(this));
        viewer.addEventListener('render.pass.perspective_overlay', this.render.bind(this));
        viewer.addEventListener('scene_changed', this.onSceneChange.bind(this));

        viewer.scene.addEventListener('measurement_added', this.onAdd);
        viewer.scene.addEventListener('measurement_removed', this.onRemove);

        this.domRoot = viewer.renderer.domElement.parentElement;
        this.elApplyMeasurement = this.createApplyMeasurementButton();
        this.domRoot.appendChild(this.elApplyMeasurement);

        this.elDeleteMeasurement = this.createDeleteMeasurementButton();
        this.domRoot.appendChild(this.elDeleteMeasurement);
    }

    createApplyMeasurementButton() {
        let elApplyMeasurement = document.createElement('input');
        elApplyMeasurement.type = 'image';
        elApplyMeasurement.title = 'Apply measurement';
        elApplyMeasurement.value = 'applyMeasurement';
        elApplyMeasurement.id = 'applyMeasurement';
        elApplyMeasurement.src = Potree.resourcePath + '/icons/green-check.svg';
        elApplyMeasurement.style.position = 'absolute';
        const potreeRenderArea = document.getElementById(this.viewer.potreeId);
        if (potreeRenderArea) {
            elApplyMeasurement.style.right = '50%';
            elApplyMeasurement.style.transform = 'translateX(50%)';
            elApplyMeasurement.style.marginRight = '20px';
        }
        elApplyMeasurement.style.bottom = '10px';
        elApplyMeasurement.style.zIndex = '10000';
        elApplyMeasurement.style.fontSize = '2em';
        elApplyMeasurement.style.background = 'rgb(238, 238, 238, 0.8)';
        elApplyMeasurement.style.height = '36px';
        elApplyMeasurement.style.width = '36px';
        elApplyMeasurement.style.borderRadius = '5px';
        elApplyMeasurement.addEventListener('click', () => {
            this.cancel.callback();
            if (
                !(
                    this.viewer &&
                    this.viewer.images360Loader &&
                    this.viewer.images360Loader.loader &&
                    this.viewer.images360Loader.loader.panoramicFactor
                )
            )
                this.cancel.addDialog();
        });
        elApplyMeasurement.style.display = 'none';
        return elApplyMeasurement;
    }

    createDeleteMeasurementButton() {
        let elDeleteMeasurement = document.createElement('input');
        elDeleteMeasurement.type = 'image';
        elDeleteMeasurement.title = 'Delete measurement';
        elDeleteMeasurement.value = 'deleteMeasurement';
        elDeleteMeasurement.id = 'deleteMeasurement';
        elDeleteMeasurement.src = Potree.resourcePath + '/icons/remove.svg';
        elDeleteMeasurement.style.position = 'absolute';
        const potreeRenderArea = document.getElementById(this.viewer.potreeId);
        if (potreeRenderArea) {
            elDeleteMeasurement.style.left = '50%';
            elDeleteMeasurement.style.marginLeft = '20px';
            elDeleteMeasurement.style.transform = 'translateX(-50%)';
        }
        elDeleteMeasurement.style.bottom = '10px';
        elDeleteMeasurement.style.zIndex = '10000';
        elDeleteMeasurement.style.fontSize = '2em';
        elDeleteMeasurement.style.background = 'rgb(238, 238, 238, 0.8)';
        elDeleteMeasurement.style.height = '36px';
        elDeleteMeasurement.style.width = '36px';
        elDeleteMeasurement.style.borderRadius = '5px';
        elDeleteMeasurement.addEventListener('click', () => {
            this.cancel.callback();
            this.scene.remove(this.measure);
        });
        elDeleteMeasurement.style.display = 'none';
        return elDeleteMeasurement;
    }

    disableElButton(elButton) {
        elButton.disabled = true;
        elButton.style.color = 'darkgrey';
        elButton.style.backgroundColor = 'grey';
        elButton.style.opacity = '0.3';
    }

    enableElButton(elButton) {
        elButton.disabled = false;
        elButton.style.color = '';
        elButton.style.backgroundColor = 'rgb(238, 238, 238, 0.8)';
        elButton.style.opacity = '';
    }

    onSceneChange(e) {
        if (e.oldScene) {
            e.oldScene.removeEventListener('measurement_added', this.onAdd);
            e.oldScene.removeEventListener('measurement_removed', this.onRemove);
        }

        e.scene.addEventListener('measurement_added', this.onAdd);
        e.scene.addEventListener('measurement_removed', this.onRemove);
    }

    startInsertion(args = {}, addDialog, customCallback) {
        let domElement = this.viewer.renderer.domElement;

        if (!args.calibrating) {
            this.elApplyMeasurement.style.display = 'block';
            this.elDeleteMeasurement.style.display = 'block';
        }

        let hasFocusedImage = this.viewer.scene.images360.some((x) => x.focusedImage !== undefined && x.focusedImage !== null);

        if (hasFocusedImage) {
            this.elApplyMeasurement.style.marginBottom = '40px';
            this.elDeleteMeasurement.style.marginBottom = '40px';
        }

        let measure;
        if (args.ifcHelper) {
            measure = new Measure(viewer, args.calibrating, args.ifcHelper);
        } else {
            measure = new Measure(viewer, args.calibrating);
        }

        this.measure = measure;

        this.dispatchEvent({
            type: 'start_inserting_measurement',
            measure: measure,
        });

        const pick = (defaul, alternative) => {
            if (defaul != null) {
                return defaul;
            } else {
                return alternative;
            }
        };

        measure.showDistances = args.calibrating ? false : args.showDistances === null ? true : args.showDistances;

        measure.showArea = pick(args.showArea, false);
        measure.showAngles = pick(args.showAngles, false);
        measure.showCoordinates = pick(args.showCoordinates, false);
        measure.showHeight = pick(args.showHeight, false);
        measure.showCircle = pick(args.showCircle, false);
        measure.showAzimuth = pick(args.showAzimuth, false);
        measure.showEdges = pick(args.showEdges, true);
        measure.closed = pick(args.closed, false);
        measure.maxMarkers = pick(args.maxMarkers, Infinity);

        if (args.calibrating) {
            measure.name = 'Calibration';
        } else if (
            this.viewer &&
            this.viewer.images360Loader &&
            this.viewer.images360Loader.loader &&
            this.viewer.images360Loader.loader.panoramicFactor
        ) {
            measure.name = 'Panoramic_Measure';
        } else {
            measure.name = args.name || 'Measurement';
        }

        this.disableElButton(this.elApplyMeasurement);

        this.scene.add(measure);

        this.cancel = {
            removeLastMarker: measure.maxMarkers > 3,
            callback: null,
            addDialog: addDialog,
        };

        let insertionCallback = (e) => {
            if (e.button === THREE.MOUSE.LEFT) {
                if (measure.spheres.length === 1 && !measure.spheres[0].visible) {
                    this.scene.remove(measure);
                    this.cancel.callback();
                    return;
                }

                if (
                    ((this.measure._showArea || this.measure._showAngles) && this.measure.points.length < 3) ||
                    (!this.measure._showArea && !this.measure._showAngles && this.measure.points.length < 2)
                ) {
                    this.disableElButton(this.elApplyMeasurement);
                } else {
                    this.enableElButton(this.elApplyMeasurement);
                }
                measure.addMarker(measure.points[measure.points.length - 1].position.clone());

                if (measure.points.length >= measure.maxMarkers) {
                    domElement.removeEventListener('mouseup', insertionCallback, false);
                    this.enableElButton(this.elApplyMeasurement);
                }

                this.viewer.inputHandler.startDragging(measure.spheres[measure.spheres.length - 1]);

                if (args.calibrating && measure.points.length > 2) {
                    this.cancel.addDialog();
                    this.cancel.callback();
                }
            } else if (e.button === THREE.MOUSE.RIGHT) {
                // In order to determine if we have a measurement to save three dots are needed two for the line and one for the floating point
                if (measure.spheres.length < 3) {
                    this.scene.remove(measure);
                } else {
                    if (
                        !(
                            this.viewer &&
                            this.viewer.images360Loader &&
                            this.viewer.images360Loader.loader &&
                            this.viewer.images360Loader.loader.panoramicFactor
                        )
                    )
                        this.cancel.addDialog();
                }
                this.cancel.callback();
            }
        };

        this.cancel.callback = (e, unFocus = false) => {
            if (customCallback) {
                customCallback();
            }
            if (this.cancel.removeLastMarker && !unFocus) {
                if (this.measure._showArea && this.measure.points.length < 4) {
                    this.scene.remove(this.measure);
                } else {
                    this.measure.removeMarker(this.measure.points.length - 1);
                }
            }
            domElement.removeEventListener('mouseup', insertionCallback, false);
            this.viewer.removeEventListener('cancel_insertions', this.cancel.callback);

            this.elDeleteMeasurement.style.display = 'none';
            this.elApplyMeasurement.style.display = 'none';
        };

        if (measure.maxMarkers > 1) {
            this.viewer.addEventListener('cancel_insertions', this.cancel.callback);
            domElement.addEventListener('mouseup', insertionCallback, false);
        }

        measure.addMarker(new THREE.Vector3(0, 0, 0));
        measure.spheres[measure.spheres.length - 1].visible = false;
        this.viewer.inputHandler.startDragging(measure.spheres[measure.spheres.length - 1]);

        this.viewer.scene.addMeasurement(measure);

        return measure;
    }

    update() {
        let camera = this.viewer.scene.getActiveCamera();
        let domElement = this.renderer.domElement;
        let measurements = this.viewer.scene.measurements;

        const renderAreaSize = this.renderer.getSize(new THREE.Vector2());
        let clientWidth = renderAreaSize.width;
        let clientHeight = renderAreaSize.height;

        this.light.position.copy(camera.position);

        // make size independant of distance
        for (let measure of measurements) {
            measure.lengthUnit = this.viewer.lengthUnit;
            measure.lengthUnitDisplay = this.viewer.lengthUnitDisplay;
            measure.update();

            updateAzimuth(this.viewer, measure);

            // spheres
            for (let sphere of measure.spheres) {
                let distance = camera.position.distanceTo(sphere.getWorldPosition(new THREE.Vector3()));
                let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
                let scale = 15 / pr;
                sphere.scale.set(scale, scale, scale);
            }

            // labels
            let labels = measure.edgeLabels.concat(measure.angleLabels);
            for (let label of labels) {
                let distance = camera.position.distanceTo(label.getWorldPosition(new THREE.Vector3()));
                let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
                let scale = 70 / pr;

                if (Potree.debug.scale) {
                    scale = Potree.debug.scale / pr;
                }

                label.scale.set(scale, scale, scale);
            }

            // coordinate labels
            for (let j = 0; j < measure.coordinateLabels.length; j++) {
                let label = measure.coordinateLabels[j];
                let sphere = measure.spheres[j];

                let distance = camera.position.distanceTo(sphere.getWorldPosition(new THREE.Vector3()));

                let screenPos = sphere.getWorldPosition(new THREE.Vector3()).clone().project(camera);
                screenPos.x = Math.round(((screenPos.x + 1) * clientWidth) / 2);
                screenPos.y = Math.round(((-screenPos.y + 1) * clientHeight) / 2);
                screenPos.z = 0;
                screenPos.y -= 30;

                let labelPos = new THREE.Vector3((screenPos.x / clientWidth) * 2 - 1, -(screenPos.y / clientHeight) * 2 + 1, 0.5);
                labelPos.unproject(camera);
                if (this.viewer.scene.cameraMode == CameraMode.PERSPECTIVE) {
                    let direction = labelPos.sub(camera.position).normalize();
                    labelPos = new THREE.Vector3().addVectors(camera.position, direction.multiplyScalar(distance));
                }
                label.position.copy(labelPos);
                let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
                let scale = 70 / pr;
                label.scale.set(scale, scale, scale);
            }

            // height label
            if (measure.showHeight) {
                let label = measure.heightLabel;

                {
                    let distance = label.position.distanceTo(camera.position);
                    let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
                    let scale = 70 / pr;
                    label.scale.set(scale, scale, scale);
                }

                {
                    // height edge
                    let edge = measure.heightEdge;

                    let sorted = measure.points.slice().sort((a, b) => a.position.z - b.position.z);
                    let lowPoint = sorted[0].position.clone();
                    let highPoint = sorted[sorted.length - 1].position.clone();
                    let min = lowPoint.z;
                    let max = highPoint.z;

                    let start = new THREE.Vector3(highPoint.x, highPoint.y, min);
                    let end = new THREE.Vector3(highPoint.x, highPoint.y, max);

                    let lowScreen = lowPoint.clone().project(camera);
                    let startScreen = start.clone().project(camera);
                    let endScreen = end.clone().project(camera);

                    let toPixelCoordinates = (v) => {
                        let r = v.clone().addScalar(1).divideScalar(2);
                        r.x = r.x * clientWidth;
                        r.y = r.y * clientHeight;
                        r.z = 0;

                        return r;
                    };

                    let lowEL = toPixelCoordinates(lowScreen);
                    let startEL = toPixelCoordinates(startScreen);
                    let endEL = toPixelCoordinates(endScreen);

                    let lToS = lowEL.distanceTo(startEL);
                    let sToE = startEL.distanceTo(endEL);

                    edge.geometry.lineDistances = [0, lToS, lToS, lToS + sToE];
                    edge.geometry.lineDistancesNeedUpdate = true;

                    edge.material.dashSize = 10;
                    edge.material.gapSize = 10;
                }
            }

            {
                // area label
                let label = measure.areaLabel;
                let distance = label.position.distanceTo(camera.position);
                let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

                let scale = 70 / pr;
                label.scale.set(scale, scale, scale);
            }

            {
                // radius label
                let label = measure.circleRadiusLabel;
                let distance = label.position.distanceTo(camera.position);
                let pr = Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

                let scale = 70 / pr;
                label.scale.set(scale, scale, scale);
            }

            {
                // edges
                const materials = [
                    measure.circleRadiusLine.material,
                    ...measure.edges.map((e) => e.material),
                    measure.heightEdge.material,
                    measure.circleLine.material,
                ];

                for (const material of materials) {
                    material.resolution.set(clientWidth, clientHeight);
                }
            }

            if (!this.showLabels) {
                const labels = [
                    ...measure.sphereLabels,
                    ...measure.edgeLabels,
                    ...measure.angleLabels,
                    ...measure.coordinateLabels,
                    measure.heightLabel,
                    measure.areaLabel,
                    measure.circleRadiusLabel,
                ];

                for (const label of labels) {
                    label.visible = false;
                }
            }
        }
    }

    render() {
        this.viewer.renderer.render(this.scene, this.viewer.scene.getActiveCamera());
    }
}
